import { createContext, useContext, useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getBusinesses } from '../api'
import type { Business } from '../types'

const BIZ_KEY = 'pb-selected-biz-id'

interface BusinessCtx {
  businesses:          Business[]
  selectedBusinessId:  number | null
  selectedBusiness:    Business | null
  setSelectedBusiness: (id: number) => void
  isLoading:           boolean
}

const BusinessContext = createContext<BusinessCtx>({
  businesses:          [],
  selectedBusinessId:  null,
  selectedBusiness:    null,
  setSelectedBusiness: () => {},
  isLoading:           false,
})

export function BusinessProvider({ children }: { children: React.ReactNode }) {
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const stored = localStorage.getItem(BIZ_KEY)
    return stored ? parseInt(stored, 10) : null
  })

  const { data: businesses = [], isLoading } = useQuery({
    queryKey: ['businesses'],
    queryFn:  getBusinesses,
    staleTime: 300_000,
  })

  // Auto-select first business when loaded if none stored or stored id is invalid
  useEffect(() => {
    if (isLoading || businesses.length === 0) return
    const valid = businesses.some(b => b.businessID === selectedId)
    if (!valid) {
      const first = businesses[0].businessID
      setSelectedId(first)
      localStorage.setItem(BIZ_KEY, String(first))
    }
  }, [businesses, isLoading, selectedId])

  const setSelectedBusiness = (id: number) => {
    setSelectedId(id)
    localStorage.setItem(BIZ_KEY, String(id))
  }

  const selectedBusiness = businesses.find(b => b.businessID === selectedId) ?? businesses[0] ?? null

  return (
    <BusinessContext.Provider value={{
      businesses,
      selectedBusinessId: selectedBusiness?.businessID ?? null,
      selectedBusiness,
      setSelectedBusiness,
      isLoading,
    }}>
      {children}
    </BusinessContext.Provider>
  )
}

export const useBusinessContext = () => useContext(BusinessContext)
